import { useState, useCallback } from 'react'
import { improveText } from '../services/api/claude'
import type { ImproveTextRequest, ImproveTextResponse } from '../types/api'
import { useAuth } from '../contexts/AuthContext'
import { useTranslation } from '../contexts/LanguageContext'
import toast from 'react-hot-toast'

interface UseAiSuggestionsReturn {
  isLoading: boolean
  error: string | null
  suggestion: ImproveTextResponse | null
  getSuggestion: (request: ImproveTextRequest) => Promise<ImproveTextResponse | null>
  reset: () => void
}

export function useAiSuggestions(): UseAiSuggestionsReturn {
  const { user } = useAuth()
  const { t } = useTranslation()
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [suggestion, setSuggestion] = useState<ImproveTextResponse | null>(null)

  const reset = useCallback(() => {
    setError(null)
    setSuggestion(null)
  }, [])

  const getSuggestion = useCallback(
    async (request: ImproveTextRequest): Promise<ImproveTextResponse | null> => {
      if (!user) {
        toast.error(t.aiReview.loginToUseAI)
        return null
      }

      if (!request.text.trim()) {
        toast.error(t.aiReview.noTextToImprove)
        return null
      }

      setIsLoading(true)
      setError(null)

      try {
        const result = await improveText(request)
        setSuggestion(result)
        return result
      } catch (err) {
        const message =
          err instanceof Error ? err.message : t.aiReview.suggestionError
        setError(message)
        toast.error(message)
        return null
      } finally {
        setIsLoading(false)
      }
    },
    [user, t]
  )

  return {
    isLoading,
    error,
    suggestion,
    getSuggestion,
    reset,
  }
}
